import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { getPromoCodeById, updatePromoCode } from '../../utils/api/promoCode';
import { toast } from 'sonner';

function UpdatePromoCode({ id, show, onClose }) {
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id || !show) return;
    setLoading(true);
    getPromoCodeById(id)
      .then((res) => {
        const data = res?.data ?? {};
        setFormData({
          code: data.code ?? '',
          type: data.type ?? 'flat',
          minPurchaseMinutes: data.minPurchaseMinutes ?? '', 
          expiresAt: data.expiresAt ? data.expiresAt.slice(0, 16) : '',
          usageLimit: data.usageLimit ?? '',       
          createdByAdminId: data.createdByAdminId ?? '',
          isActive: data.isActive ?? false,    
          value: data.value ?? '',  
        });
      })
      .catch((err) => {
        console.error("Error fetching promo code:", err);
        toast.error(err?.message || "Failed to load promo code");
        onClose?.();
      })
      .finally(() => setLoading(false));
  }, [id, show, onClose]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData) return;

    const { code, type, value, minPurchaseMinutes, usageLimit, createdByAdminId, expiresAt } = formData;

    if (
      (code ?? '').toString().trim() === '' ||
      (type ?? '').toString().trim() === '' ||
      value === '' ||
      minPurchaseMinutes === '' ||
      usageLimit === '' ||
      (createdByAdminId ?? '').toString().trim() === ''
    ) {
      toast.error('Please fill all required fields.');
      return;
    }

    // Build payload with proper types
    const payload = {
      ...formData,
      value: Number(value),
      minPurchaseMinutes: Number(minPurchaseMinutes),
      usageLimit: Number(usageLimit),
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
    };

    try {
      setSaving(true);
      const res = await updatePromoCode(id, payload);
      toast.success(res?.message || "Promo code updated successfully!");
      onClose?.();
    } catch (err) {
      console.error("Update failed:", err);
      toast.error(err?.message || "Failed to update promo code");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <motion.div
      className="modal fade show"
      tabIndex="-1"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        display: 'block',
        backgroundColor: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
        zIndex: 1050,
      }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content" style={{
          borderRadius: '16px',
          border: 'none',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}>
          <div className="modal-header">
            <h6 className="modal-title">Update Promo Code</h6>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {loading || !formData ? (
                <div className="text-center flex gap-10">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div
                      key={i}
                      className="shimmer shimmer-text"
                      style={{ width: '120px', height: '2.5rem', margin: 'auto' }}
                    />
                  ))}
                </div>
              ) : (
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Promo Code*</label>
                    <input
                      type="text"
                      name="code"
                      className="form-control"
                      value={formData.code}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Type*</label>
                    <select
                      name="type"
                      className="form-select"
                      value={formData.type}
                      onChange={handleChange}
                      required
                    >
                      <option value="flat">Flat</option>
                      <option value="percentage">Percentage</option>
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Value*</label>
                    <input
                      type="number"
                      name="value"
                      className="form-control"
                      value={formData.value}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Min Purchase Minutes*</label>
                    <input
                      type="number"
                      name="minPurchaseMinutes"
                      className="form-control"
                      value={formData.minPurchaseMinutes}
                      onChange={handleChange}
                      required
                      min={0}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Usage Limit*</label>
                    <input
                      type="number"
                      name="usageLimit"
                      className="form-control"
                      value={formData.usageLimit}
                      onChange={handleChange}
                      required
                      min={0}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Expires At</label>
                    <input
                      type="datetime-local"
                      name="expiresAt"
                      className="form-control"
                      value={formData.expiresAt}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-12 d-flex flex-column">
                    <label className="form-label fw-semibold">Active Status</label>
                    <div className="d-flex align-items-center">
                      <label
                        className="switch"
                        style={{
                          position: 'relative',
                          display: 'inline-block',
                          width: '50px',
                          height: '24px',
                          marginBottom: 0
                        }}
                      >
                        <input
                          type="checkbox"
                          name="isActive"
                          checked={!!formData.isActive}
                          onChange={handleChange}
                          style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span
                          style={{
                            position: 'absolute',
                            cursor: 'pointer',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: formData.isActive ? '#28a745' : '#ccc',
                            transition: '.4s',
                            borderRadius: '34px'
                          }}
                        >
                          <span
                            style={{
                              position: 'absolute',
                              height: '18px',
                              width: '18px',
                              left: formData.isActive ? '26px' : '4px',
                              top: '3px',
                              backgroundColor: 'white',
                              transition: '.4s',
                              borderRadius: '50%'
                            }}
                          />
                        </span>
                      </label>
                      <span className="ms-3 fw-semibold" style={{ minWidth: '60px' }}>
                        {formData.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  <div className="col-md-12">
                    <label className="form-label fw-semibold">Created By (Admin ID)*</label>
                    <input
                      type="text"
                      name="createdByAdminId"
                      className="form-control"
                      value={formData.createdByAdminId}
                      onChange={handleChange}
                      required
                      readOnly
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-success"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Update Promo Code'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </motion.div>
  );
}

export default UpdatePromoCode;
